import Konva from "konva";
import { Options, useHotkeys } from "react-hotkeys-hook";
import { useMapCanvasShapes } from "../../../contexts/MapCanvasContext";
import { useMapCanvasStage } from "../../../contexts/MapCanvasContext/useMapCanvasStage";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import { useMapEditorSelection } from "../../../contexts/MapEditorContext";
import { WallDoor, WallShape, WallPathShape } from "../../../../../types";
import { useEditorShortcuts } from "../EditorShortcutContext/useEditorShortcuts";
import { GLOBAL_SHORTCUTS_SCOPE } from "../shortcuts";

/** Find the door on a WallShape closest to the cursor */
const findClosestDoorOnWall = (
  cursor: { x: number; y: number },
  wall: WallShape
): WallDoor | null => {
  if (!wall.doors || wall.doors.length === 0) return null;

  const dx = wall.x2 - wall.x;
  const dy = wall.y2 - wall.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) return wall.doors[0];

  const ux = dx / length;
  const uy = dy / length;

  let bestDoor: WallDoor | null = null;
  let bestDistSq = Infinity;

  for (const door of wall.doors) {
    const doorX = wall.x + door.position * dx;
    const doorY = wall.y + door.position * dy;
    const distSq = (cursor.x - doorX) ** 2 + (cursor.y - doorY) ** 2;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestDoor = door;
    }
  }

  return bestDoor;
};

/** Find the door on a WallPathShape closest to the cursor */
const findClosestDoorOnWallPath = (
  cursor: { x: number; y: number },
  path: WallPathShape
): { edgeIndex: number; doorId: string } | null => {
  const { vertices, closed } = path;
  const edgeCount = closed ? vertices.length : vertices.length - 1;

  let bestEdgeIndex = -1;
  let bestDoorId = "";
  let bestDistSq = Infinity;

  for (let i = 0; i < edgeCount; i++) {
    const edge = path.edges[i];
    if (!edge?.doors || edge.doors.length === 0) continue;

    const from = vertices[i];
    const to = vertices[(i + 1) % vertices.length];
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    for (const door of edge.doors) {
      const doorX = from.x + door.position * dx;
      const doorY = from.y + door.position * dy;
      const distSq = (cursor.x - doorX) ** 2 + (cursor.y - doorY) ** 2;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        bestEdgeIndex = i;
        bestDoorId = door.id;
      }
    }
  }

  if (bestEdgeIndex === -1) return null;
  return { edgeIndex: bestEdgeIndex, doorId: bestDoorId };
};

export const useRemoveDoorShortcut = (
  stageRef: React.RefObject<Konva.Stage | null>
) => {
  const { selectedIds } = useMapEditorSelection();
  const { updateShape } = useMapEditorActions();
  const { shapes } = useMapCanvasShapes();
  const { stagePositionRef } = useMapCanvasStage();
  const { shortcuts } = useEditorShortcuts();

  const selectedShape =
    selectedIds.length === 1
      ? shapes.find(
          (s) => s.id === selectedIds[0] && (s.type === "wall" || s.type === "wall-path")
        )
      : undefined;

  const shortcutOptions: Options = {
    scopes: [GLOBAL_SHORTCUTS_SCOPE],
    enabled: !!selectedShape,
    preventDefault: true
  };

  useHotkeys(
    shortcuts.global.removeDoor,
    () => {
      if (!selectedShape) return;

      const stage = stageRef.current;
      if (!stage) return;

      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      const stagePosition = stagePositionRef.current;
      const cursor = {
        x: (pointerPos.x - stagePosition.x) / stagePosition.scale,
        y: (pointerPos.y - stagePosition.y) / stagePosition.scale
      };

      if (selectedShape.type === "wall") {
        const wall = selectedShape as WallShape;
        const closestDoor = findClosestDoorOnWall(cursor, wall);
        if (!closestDoor) return;
        const doors = (wall.doors || []).filter((d) => d.id !== closestDoor.id);
        updateShape(wall.id, { doors } as Partial<WallShape>);
      } else if (selectedShape.type === "wall-path") {
        const path = selectedShape as WallPathShape;
        const result = findClosestDoorOnWallPath(cursor, path);
        if (!result) return;
        const newEdges = [...path.edges];
        newEdges[result.edgeIndex] = {
          ...newEdges[result.edgeIndex],
          doors: (newEdges[result.edgeIndex].doors || []).filter(
            (d) => d.id !== result.doorId
          )
        };
        updateShape(path.id, { edges: newEdges } as Partial<WallPathShape>);
      }
    },
    shortcutOptions,
    [shortcuts.global.removeDoor, selectedShape]
  );
};
